import {
  Component,
  DestroyRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Input,
  signal,
  CUSTOM_ELEMENTS_SCHEMA,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";
import { AccountService } from "app/shared/services/account.service";
import {
  CariButtonComponent,
  emailRegex,
  UIComponentsModule,
} from "@cariloop/ui-components";
import { HttpErrorResponse } from "@angular/common/http";
import {
  DevicePlatformService,
  MixPanelPlatform,
} from "app/shared/services/device-platform.service";
import { AnalyticsService } from "@cariloop/analytics";
import { Router } from "@angular/router";
import {
  LangChangeEvent,
  TranslateService,
  TranslateModule,
} from "@ngx-translate/core";
import { Title } from "@angular/platform-browser";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BrazeService } from "app/shared/services/braze.service";
import { IntlTelData } from "app/shared/types/intl-tel-data.type";

type ErrorType = "email-already-used" | "generic-error" | "gateway-timeout";

type State = {
  hasServerSideError: boolean;
  errorType: ErrorType;
  isSubmitting: boolean;
};

@Component({
  selector: "app-backup-contact",
  templateUrl: "./backup-contact.component.html",
  styleUrls: ["./backup-contact.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    UIComponentsModule,
    CariButtonComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BackupContactComponent implements OnInit, OnDestroy {
  @Output() nextStep = new EventEmitter<void>();
  @Input() enrollmentCode: string | undefined;
  backupForm: FormGroup = new FormGroup({
    phone: new FormControl("", [Validators.required]),
    email: new FormControl("", [Validators.pattern(emailRegex)]),
    method: new FormControl(false),
    consentMarketing: new FormControl(true),
  });
  state: State = {
    hasServerSideError: false,
    errorType: undefined,
    isSubmitting: false,
  };
  platform: MixPanelPlatform;
  lang: string;
  currentRadioSelected: string;
  showEmailInput = signal<boolean>(true);
  dialingCode = signal({ country: "", code: "" });
  initialCountry = "us";

  constructor(
    private accountService: AccountService,
    private analyticsService: AnalyticsService,
    private devicePlatformService: DevicePlatformService,
    private translateService: TranslateService,
    private titleService: Title,
    private brazeService: BrazeService,
    private destroyRef: DestroyRef,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    const locale = new Intl.Locale(navigator.language);
    this.initialCountry = locale.region.toLowerCase() || "us";
    this.translateService
      .stream("REGISTER.PAGE_TITLE")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pageTitle) => {
        this.titleService.setTitle(pageTitle + " | Cariloop");
      });

    this.lang =
      this.translateService.getCurrentLang() ||
      this.translateService.getFallbackLang();
    this.translateService.onLangChange.subscribe((langObj: LangChangeEvent) => {
      this.lang = langObj.lang;
    });
    const accountResult = this.accountService.getLogged();
    if (accountResult instanceof Error) {
      this.router.navigate(["/"], { queryParamsHandling: "preserve" });
      return;
    }

    const { id, emailType } = accountResult;
    if (emailType === "personal") {
      this.showEmailInput.set(false);
      this.backupForm.get("email").clearValidators();
      this.backupForm.get("email").updateValueAndValidity();
    }

    this.backupForm.get("method").valueChanges.subscribe((val) => {
      if (val) {
        this.backupForm.controls["email"].setValidators([
          Validators.pattern(emailRegex),
        ]);
        this.backupForm.get("phone").clearValidators();
        this.backupForm.get("phone").updateValueAndValidity();
        this.backupForm.controls["email"].updateValueAndValidity();
        this.currentRadioSelected = "email";
      } else {
        this.backupForm.controls["phone"].setValidators([Validators.required]);
        this.backupForm.get("email").clearValidators();
        this.backupForm.get("email").updateValueAndValidity();
        this.backupForm.controls["phone"].updateValueAndValidity();
        this.currentRadioSelected = "phone";
      }
    });

    this.platform = this.devicePlatformService.getPlatformForMixpanel();

    this.analyticsService.trackEvent("back-up-contact", {
      platform: this.platform,
      source: "auth",
      accountId: id,
      userFlow: "create-new-account",
      enrollmentCode: this.enrollmentCode,
    });
  }

  validatePhoneInput(intlTelData: IntlTelData): void {
    const country = intlTelData?.country;
    this.dialingCode.set({ country: country?.iso2, code: country?.dialCode });
  }

  ngOnDestroy(): void {
    this.titleService.setTitle("Cariloop");
  }

  onContinue() {
    const accountResult = this.accountService.getLogged();
    if (accountResult instanceof Error) {
      throw accountResult;
    }

    const { id } = accountResult;
    const data = {};

    data["phone"] = this.backupForm.value.phone;
    if (!this.backupForm.value.method) {
      const phoneCtrl = this.backupForm.get("phone");
      if (!phoneCtrl.value) {
        phoneCtrl.markAsTouched();
        phoneCtrl.setErrors({ ...(phoneCtrl.errors ?? {}), required: true });
        return;
      }
      if (!phoneCtrl.valid) {
        phoneCtrl.markAsTouched();
        return;
      }

      data["phone"] = phoneCtrl.value;
      data["dialingCode"] = {
        country: this.dialingCode().country,
        code: this.dialingCode().code,
      };
    }

    const emailCtrl = this.backupForm.get("email");
    if (emailCtrl.value && !emailCtrl.valid) {
      emailCtrl.markAsTouched();
      return;
    }

    if (emailCtrl.value && this.showEmailInput()) {
      data["email2"] = this.backupForm.value.email;
      data["email2Type"] = "personal";
    }
    data["notificationConfigs"] = {
      email: true,
      push: true,
      ...(this.backupForm.value.consentMarketing
        ? { optInDate: new Date().toISOString() }
        : { optOutDate: new Date().toISOString() }),
    };

    if (this.backupForm.value.consentMarketing) {
      this.brazeService.smsOptInForUser(id, this.backupForm.value.phone);
    } else {
      this.brazeService.smsOptOutForUser(id, this.backupForm.value.phone);
    }

    this.state = {
      hasServerSideError: false,
      errorType: undefined,
      isSubmitting: true,
    };

    this.accountService.updateAttributes(id, data).subscribe({
      next: () => {
        this.analyticsService.trackEvent("back-up-contact-complete", {
          platform: this.platform,
          source: "auth",
          accountId: id,
          userFlow: "create-new-account",
          enrollmentCode: this.enrollmentCode,
        });

        this.analyticsService.trackEvent("Marketing_SMS_preference_added", {
          platform: this.platform,
          source: "auth",
          accountId: id,
          userFlow: "create-new-account",
          status: this.backupForm.value.consentMarketing ? "opt_in" : "opt_out",
        });

        this.nextStep.next();
        this.state = {
          ...this.state,
          isSubmitting: false,
        };
      },
      error: (errorResponse: HttpErrorResponse) => {
        const error = {
          ...errorResponse.error.error,
        };
        let errorType: ErrorType = "generic-error";

        if (
          error.statusCode === 422 &&
          error.message === "Secondary email is already in use."
        ) {
          errorType = "email-already-used";
        } else if (errorResponse.status === 504) {
          errorType = "gateway-timeout";
        }

        this.state = {
          ...this.state,
          isSubmitting: false,
          hasServerSideError: true,
          errorType,
        };
      },
    });
  }
}
